// use client: router.push handlers on each notice row
'use client'

import type { HomeNotice } from '@/types/cms'

import { Container, Tab, Tabs } from 'react-bootstrap'
import moment from 'moment'
import { useRouter } from 'next/navigation'

import arrow_right_circle from '@/assets/images/arrow_right_circle.svg'
import { PrimaryButton } from '@/components/buttons'

function ImportantNotice({
  initialData = null
}: {
  initialData?: { important?: HomeNotice[]; serviceUpdates?: HomeNotice[] } | null
}) {
  const router = useRouter()
  // Seeded at build time so the notices/service-updates render in the static HTML.
  // `important` already includes the legacy JSON notices, merged in app/page.tsx.
  // Content comes from the build-time snapshot only. Every caller now seeds
  // `initialData`, so the old `if (!initialData)` fetch branch was dead. A Strapi
  // publish rebuilds and redeploys the site, so there is nothing to revalidate on
  // mount. See tests/no-client-cms-fetch.spec.ts.
  const importantNoticeListCms: HomeNotice[] = initialData?.important ?? []
  const serviceUpdatesList: HomeNotice[] = initialData?.serviceUpdates ?? []

  const handleNavigate = notice => {
    if (notice.type === 'important') {
      router.push(`/important-notice-detail/${notice.slug}`)
    } else if (notice.type === 'service') {
      router.push(`/service-update-detail/${notice.slug}`)
    }
  }

  const renderList = (list, viewAllPath) => {
    // item.date is already formatted to 'DD MMM YYYY' upstream; parse it back
    // with that explicit format. Bare moment(str) on a non-ISO string falls
    // back to an unreliable, locale-dependent Date (logs a deprecation warning
    // and can sort differently on server vs client).
    const sortedItems = [...list]
      .sort(
        (a, b) => moment(b.date, 'DD MMM YYYY').valueOf() - moment(a.date, 'DD MMM YYYY').valueOf()
      )
      .slice(0, 3)

    return (
      <>
        <div
          style={{
            background: '#fff',
            border: '1px solid #f4f6f9',
            borderRadius: '14px',
            boxShadow: '0px 20px 24px 0px rgba(16,24,40,0.08), 0px 8px 8px 0px rgba(16,24,40,0.03)',
            padding: '24px'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {sortedItems.flatMap((item, index) => {
              const row = (
                <div
                  key={item.slug}
                  className="item"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleNavigate(item)}
                >
                  <div className="number" style={{ marginRight: 16 }}>
                    {`${index + 1}`.padStart(2, '0')}
                  </div>
                  <div
                    className="description d-flex justify-content-between align-items-start"
                    style={{
                      borderLeft: `2px solid ${item.type === 'important' ? '#FEC84B' : '#FDA29B'}`
                    }}
                  >
                    <div className="d-flex flex-column flex-grow-1">
                      <span className="title fw-medium">{item.title}</span>
                      <span className="date mt-1">{item.date}</span>
                    </div>
                    <img
                      src={arrow_right_circle.src}
                      width="20"
                      className="cursor-pointer ms-2"
                      alt="arrow-right"
                    />
                  </div>
                </div>
              )
              return index < sortedItems.length - 1
                ? [
                    row,
                    <hr
                      key={`divider-${index}`}
                      style={{ margin: 0, borderColor: '#e4e7ec', opacity: 1 }}
                    />
                  ]
                : [row]
            })}
          </div>
        </div>
        <div className="pt-5 text-center">
          <PrimaryButton text="View all" href={viewAllPath} />
        </div>
      </>
    )
  }

  return (
    <section className="section important-notice">
      <div className="container w-100 w-lg-75">
        <Container>
          <Tabs
            defaultActiveKey="important"
            className="justify-content-center custom-tabs border-0 nav nav-tabs pt-md-5 pt-3"
          >
            <Tab eventKey="important" title="Important notices">
              {renderList(importantNoticeListCms, '/important-notices')}
            </Tab>
            <Tab eventKey="service" title="Service updates">
              {renderList(serviceUpdatesList, '/service-updates')}
            </Tab>
          </Tabs>
        </Container>
      </div>
    </section>
  )
}

export default ImportantNotice
